import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram, faFacebook, faTiktok, faYoutube, faDiscord } from '@fortawesome/free-brands-svg-icons';
import { faTimes, faClock, faFire } from '@fortawesome/free-solid-svg-icons';
import { useProductSearch } from '../hooks/useProductSearch';
import { usePartneredBrands } from '../hooks/usePartneredBrands';
import { getRecentSearches, clearRecentSearches } from '../utils/recentSearches';
import '../StylesComponents/hamburger-menu.css';

function SidePanel({ menuOpen, setMenuOpen, searchQuery, setSearchQuery }) {
  const [recentSearches, setRecentSearches] = useState([]);
  const partneredBrands = usePartneredBrands(6);
  const {
    suggestions,
    showSuggestions,
    setShowSuggestions,
    handleSearch,
    handleSmartSearch
  } = useProductSearch(searchQuery, { onNavigate: () => setMenuOpen(false) });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSuggestions && !event.target.closest('.search-wrapper')) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSuggestions, setShowSuggestions]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
      setRecentSearches(getRecentSearches());
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [menuOpen]);

  const handleRecentClick = (term) => {
    setSearchQuery(term);
    handleSearch(term);
  };

  const handlePartnerClick = (brand) => {
    setSearchQuery(brand);
    handleSmartSearch({ type: 'partner', label: brand });
  };

  const handleClearRecent = (e) => {
    e.stopPropagation();
    clearRecentSearches();
    setRecentSearches([]);
  };

  const showEmptyState = showSuggestions && searchQuery.length < 2;
  const showResults = showSuggestions && searchQuery.length >= 2 && suggestions.length > 0;

  return (
    <>
      <div 
        className={`overlay ${menuOpen ? 'open' : ''}`} 
        onClick={() => setMenuOpen(false)}
      ></div>
      <div className={`side-panel ${menuOpen ? 'open' : ''}`}>
        <div className="panel-content" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        
        <div className="side-panel-header-technical">
          <div className="side-panel-status">
            <span className="pulse-dot"></span>
            Navigation Active
          </div>
          <h2>Menu</h2>
          <button 
            className="side-panel-close-btn" 
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="side-panel-content-technical">
          <div className="search-wrapper">
            <div className="search-bar">
              <input
                type="text"
                className="search-input"
                placeholder="SEARCH_MARKET..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoComplete="off"
                spellCheck="false"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                onFocus={() => setShowSuggestions(true)}
              />
              <button className="search-button" onClick={() => handleSearch()}>
                GO
              </button>
            </div>

            {showResults && (
              <div className="search-suggestions animate-slide-up">
                {suggestions.map((s, idx) => (
                  <div 
                    key={`${s.type}-${s.id || s.label}-${idx}`}
                    className={`suggestion-item ${s.type === 'smart' ? 'smart-match' : ''} ${s.type === 'product' ? 'product-type' : ''}`}
                    onClick={() => handleSmartSearch(s)}
                  >
                    <div className="suggestion-main">
                      {s.type === 'product' && s.image && (
                        <img src={s.image} alt="" className="suggestion-thumb" loading="lazy" />
                      )}
                      <div className="label-group">
                        <span className="label">{s.label}</span>
                        {s.type === 'smart' && <span className="sub-label">SMART MATCH</span>}
                      </div>
                    </div>
                    {s.type !== 'product' && (
                      <div className="suggestion-meta">
                        {s.count != null && <span className="suggestion-count">{s.count >= 1000 ? `${Math.round(s.count / 100) / 10}k` : s.count}</span>}
                        <span className="type-tag">{s.type.toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {showEmptyState && (
              <div className="search-suggestions search-empty-state animate-slide-up">
                {recentSearches.length > 0 && (
                  <div className="search-empty-section">
                    <div className="search-empty-header">
                      <span className="search-empty-title">
                        <FontAwesomeIcon icon={faClock} /> RECENT
                      </span>
                      <button className="search-clear-btn" onClick={handleClearRecent}>CLEAR</button>
                    </div>
                    {recentSearches.map((term, idx) => (
                      <div key={idx} className="suggestion-item" onClick={() => handleRecentClick(term)}>
                        <span className="label">{term}</span>
                        <span className="type-tag">SEARCH</span>
                      </div>
                    ))}
                  </div>
                )}
                {partneredBrands.length > 0 && (
                  <div className="search-empty-section">
                    <div className="search-empty-header">
                      <span className="search-empty-title">
                        <FontAwesomeIcon icon={faFire} /> SUGGESTIONS
                      </span>
                    </div>
                    {partneredBrands.map((brand, idx) => (
                      <div key={idx} className="suggestion-item" onClick={() => handlePartnerClick(brand)}>
                        <span className="label">{brand}</span>
                        <span className="type-tag">BRAND</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <nav className="nav-links-technical">
            <Link to="/all-brands" onClick={() => setMenuOpen(false)}>All Brands</Link>
            <Link to="/featured" onClick={() => setMenuOpen(false)}>Partnered Brands</Link>
            <Link to="/trending" onClick={() => setMenuOpen(false)}>What's Trending</Link>
            <Link to="/new-in" onClick={() => setMenuOpen(false)}>New In</Link>
            <div className="nav-divider"></div>
            <Link to="/mens" onClick={() => setMenuOpen(false)}>Men's</Link>
            <Link to="/womens" onClick={() => setMenuOpen(false)}>Women's</Link>
            <Link to="/kids" onClick={() => setMenuOpen(false)}>Kid's</Link>
            <Link to="/shoes" onClick={() => setMenuOpen(false)}>Shoes</Link>
            <Link to="/accessories" onClick={() => setMenuOpen(false)}>Accessories</Link>
            <div className="nav-divider"></div>
            <Link 
              to="#" 
              onClick={(e) => e.preventDefault()} 
              className="maintenance-mode"
              style={{ color: '#888', fontWeight: '900' }}
            >
              STOCKROOM PRO
            </Link>
            <Link to="/about" onClick={() => setMenuOpen(false)}>ABOUT US</Link>
          </nav>

          <div className="socials-section-technical">
            <h3>SOCIAL_CHANNELS</h3>
            <div className="social-links-technical">
              <a href="https://www.instagram.com/thestockroomau/" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faInstagram} /> INSTAGRAM
              </a>
              <a href="https://www.facebook.com/profile.php?id=61563686767903" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faFacebook} /> FACEBOOK
              </a>
              <a href="https://www.tiktok.com/@stockroomau" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faTiktok} /> TIKTOK
              </a>
              <a href="https://www.youtube.com" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faYoutube} /> YOUTUBE
              </a>
              <a href="https://discord.gg/q4vbdGW5" target="_blank" rel="noopener noreferrer" className="discord-link-special">
                <FontAwesomeIcon icon={faDiscord} /> DISCORD
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </>
);
}

export default SidePanel;
